import { CdkDropListGroup } from '@angular/cdk/drag-drop';
import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  forwardRef,
  inject,
  input,
  linkedSignal,
  output,
  signal,
  viewChild
} from '@angular/core';
import {
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup
} from '@angular/forms';
import { isEqual, some } from 'lodash-es';
import { MonacoEditorModule } from 'ngx-monaco-editor';
import { catchError, map, Observable, of, switchMap, throwError } from 'rxjs';
import { tap } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';
import { LoadingComponent } from '../../component/loading/loading.component';
import { SelectPluginComponent } from '../../component/select-plugin/select-plugin.component';
import { ResizeHandleDirective } from '../../directive/resize-handle.directive';
import { Oembed } from '../../model/oembed';
import { Ref } from '../../model/ref';
import { CssUrlPipe } from '../../pipe/css-url.pipe';
import { ThumbnailPipe } from '../../pipe/thumbnail.pipe';
import { AdminService } from '../../service/admin.service';
import { ScrapeService } from '../../service/api/scrape.service';
import { ConfigService } from '../../service/config.service';
import { EditorService } from '../../service/editor.service';
import { OembedStore } from '../../store/oembed';
import { Store } from '../../store/store';
import { controlState, controlValue } from '../../util/form';
import { getScheme, getTitleFromFilename } from '../../util/http';
import { addAllHierarchicalTags, hasMedia, hasPrefix, hasTag } from '../../util/tag';
import { EditorComponent } from '../editor/editor.component';
import { LinksFormComponent } from '../links/links.component';
import { pluginsForm, PluginsFormComponent, syncPluginsForm } from '../plugins/plugins.component';
import { fillTags, TagsFormComponent } from '../tags/tags.component';

@Component({
  selector: 'app-ref-form',
  templateUrl: './ref.component.html',
  styleUrls: ['./ref.component.scss'],
  host: {
    'class': 'nested-form',
    '[class.show-drops]': 'dropping()',
    '(dragenter)': 'onDragEnter()',
    '(window:dragend)': 'onDragEnd()',
    '(jasper-drag-end)': 'onDragEnd()',
    '(jasper-drag-start)': 'onCdkDragStart()',
  },
  imports: [
    forwardRef(() => EditorComponent),
    CdkDropListGroup,
    ReactiveFormsModule,
    LinksFormComponent,
    LoadingComponent,
    SelectPluginComponent,
    PluginsFormComponent,
    MonacoEditorModule,
    ResizeHandleDirective,
    TagsFormComponent,
    AsyncPipe,
    ThumbnailPipe,
    CssUrlPipe,
  ],
})
export class RefFormComponent {
  config = inject(ConfigService);
  admin = inject(AdminService);
  private editor = inject(EditorService);
  private scrape = inject(ScrapeService);
  private oembeds = inject(OembedStore);
  private store = inject(Store);
  private fb = inject(UntypedFormBuilder);
  private cd = inject(ChangeDetectorRef);
  private el = inject<ElementRef<HTMLElement>>(ElementRef);

  private readonly rootControlState = controlValue(() => this.group());

  protected readonly controlState0 = controlValue(() => this.url());
  private readonly controlState1 = controlValue(() => this.group());
  private readonly controlState2 = controlValue(() => this.sources());
  private readonly controlState3 = controlValue(() => this.tags());


  readonly origin = input<string | undefined>('');
  readonly group = input.required<UntypedFormGroup>();
  readonly creating = input(false);
  private readonly tagsValue = controlValue<string[]>(() => this.tags());
  readonly toggleTag = output<string>();

  readonly tagsFormComponent = viewChild.required<TagsFormComponent>('tagsFormComponent');
  readonly sourcesFormComponent = viewChild.required<LinksFormComponent>('sources');
  readonly altsFormComponent = viewChild.required<LinksFormComponent>('alts');
  readonly pluginsFormComponent = viewChild.required<PluginsFormComponent>('pluginsFormComponent');
  readonly editorComponent = viewChild<EditorComponent>('ed');

  readonly dropping = signal(false);

  id = 'ref-' + uuid();
  readonly oembed = signal<Oembed | undefined>(undefined);
  readonly scraped = signal<Ref | undefined>(undefined);
  readonly refInput = input<Partial<Ref> | undefined>(undefined, { alias: 'ref' });
  /**
   * The Ref the form was filled from. Plugin forms are built from its plugin data.
   */
  readonly ref = linkedSignal(() => this.refInput() as Ref | undefined);
  readonly scrapingTitle = signal(false);
  readonly scrapingPublished = signal(false);
  readonly scrapingAll = signal(false);
  readonly completedUploads = signal<Ref[]>([]);

  readonly web = computed(() => {
    this.rootControlState();
    this.controlState0();
    const scheme = getScheme(this.url().value);
    return scheme === 'http:' || scheme === 'https:';
  });

  readonly url = computed(() => {
    this.rootControlState();
    return this.group().get('url') as UntypedFormControl;
  });

  readonly title = computed(() => {
    this.rootControlState();
    return this.group().get('title') as UntypedFormControl;
  });

  readonly comment = computed(() => {
    this.rootControlState();
    return this.group().get('comment') as UntypedFormControl;
  });

  readonly published = computed(() => {
    this.rootControlState();
    return this.group().get('published') as UntypedFormControl;
  });
  protected readonly publishedRequired = controlState(this.published, c => c.touched && !!c.errors?.['required']);

  readonly tags = computed(() => {
    this.rootControlState();
    return this.group().get('tags') as UntypedFormArray;
  });

  readonly sources = computed(() => {
    this.rootControlState();
    return this.group().get('sources') as UntypedFormArray;
  });

  readonly thumbnail = computed(() => {
    this.rootControlState();
    this.controlState1();
    if (!this.admin.getPlugin('plugin/thumbnail')) return false;
    if (hasTag('plugin/thumbnail', this.group().value)) return true;
    return !!this.admin.getPlugin('plugin/image') && hasTag('plugin/image', this.group().value);
  });

  readonly thumbnailRefs = computed(() => {
    this.rootControlState();
    this.controlState1();
    return [{ ...this.group().getRawValue(), origin: this.creating() ? this.store.account.origin() : this.origin() }];
  });

  readonly thumbnailPlugin = computed(() => {
    this.rootControlState();
    this.controlState1();
    const plugin = this.group().value.plugins?.['plugin/thumbnail'];
    return plugin && typeof plugin === 'object' && !Array.isArray(plugin) ? plugin : undefined;
  });

  readonly thumbnailColor = computed(() => {
    this.rootControlState();
    return this.thumbnailPlugin()?.color || '';
  });

  readonly thumbnailEmoji = computed(() => {
    this.rootControlState();
    return this.thumbnailPlugin()?.emoji || '';
  });

  readonly thumbnailRadius = computed(() => {
    this.rootControlState();
    return this.thumbnailPlugin()?.radius || 0;
  });

  readonly top = computed(() => {
    this.rootControlState();
    this.controlState2();
    this.controlState0();
    return this.sources().value[1] || this.sources().value[0] || this.ref()?.url || this.url().value;
  });

  addSource(value = '') {
    while (this.sources().value.length < 2) {
      this.sources().push(this.fb.control(this.top(), LinksFormComponent.validators));
    }
    this.sources().push(this.fb.control(value, LinksFormComponent.validators));
  }

  setTags(value: string[]) {
    fillTags(this.fb, this.tags(), value);
  }

  readonly editorLabel = computed(() => {
    this.rootControlState();
    this.controlState3();
    // TODO: Move to config
    if (hasTag('+plugin/secret', this.tags().value)) return $localize`Secret Key`;
    if (hasTag('plugin/alt', this.tags().value)) return $localize`Alt Text`;
    return $localize`Abstract`;
  });

  readonly addEditorLabel = computed(() => {
    this.rootControlState();
    return $localize`+ Add ` + this.editorLabel().toLowerCase();
  });

  readonly addEditorTitle = computed(() => {
    this.rootControlState();
    return $localize`Add ` + this.editorLabel().toLowerCase();
  });

  readonly codeLang = computed(() => {
    this.rootControlState();
    for (const t of this.tagsValue() || []) {
      if (hasPrefix(t, 'plugin/code')) {
        return t.split('/')[2];
      }
    }
    return '';
  });

  readonly codeOptions = computed(() => ({
    language: this.codeLang(),
    theme: this.store.darkTheme() ? 'vs-dark' : 'vs',
    automaticLayout: true,
  }), { equal: isEqual });

  readonly customEditor = computed(() => {
    this.rootControlState();
    const tags = this.tagsValue();
    if (!tags) return false;
    return some(this.admin.editor(), t => hasTag(t.tag, tags));
  });

  onDragEnter() {
    this.dropping.set(true);
  }

  onDragEnd() {
    this.dropping.set(false);
  }

  onCdkDragStart() {
    this.dropping.set(true);
    // Render empty drop lists synchronously so CDK caches their positions,
    // host bindings are not updated by detectChanges
    this.el.nativeElement.classList.add('show-drops');
    this.cd.detectChanges();
  }

  validate(input: HTMLInputElement) {
    if (this.title().touched) {
      if (this.title().errors?.['required']) {
        input.setCustomValidity($localize`Title must not be blank.`);
        input.reportValidity();
      }
    }
  }

  setComment(value: string) {
    this.comment().setValue(value);
    // Ignore tags and sources from new comment
    this.editor.syncEditor(this.fb, this.group(), value);
  }

  syncEditor() {
    this.editor.syncEditor(this.fb, this.group());
  }

  get scrape$() {
    return this.scrapeUrl$();
  }

  /**
   * Scrapes the given URL, or the source of a repost, or the URL.
   * Pass the URL when it was just added to the form, as links are only
   * written to the form when it renders.
   */
  private scrapeUrl$(url?: string) {
    const scraped = this.scraped();
    if (scraped) return of(scraped);
    url ||= hasTag('plugin/repost', this.tags().value) ? this.sources().value?.[0] : this.url().value;
    return this.scrape.webScrape(url!).pipe(
      tap(s => {
        this.scraped.set(s);
        const current = this.ref();
        if (s.modified && current?.modified) {
          const ref: Ref = {
            ...current,
            modifiedString: s.modifiedString,
            modified: s.modified,
            tags: [...current.tags || []],
            plugins: { ...current.plugins || {} },
          };
          if (hasTag('_plugin/cache', s)) {
            if (!hasTag('_plugin/cache', ref)) {
              ref.tags ||= [];
              ref.tags.push('_plugin/cache');
            }
            ref.plugins ||= {}
            ref.plugins['_plugin/cache'] = s.plugins?.['_plugin/cache'];
          }
          this.setRef(ref);
        }
      }),
    );
  }

  scrapeTitle(force = false) {
    this.scrapingTitle.set(true);
    const before = this.title().value;
    (this.web() ? this.webTitle$ : of(undefined)).subscribe(title => {
      this.scrapingTitle.set(false);
      title ||= getTitleFromFilename(this.url().value) || undefined;
      const edited = force ? this.title().value !== before : this.title().dirty;
      if (title && !edited) this.group().patchValue({ title });
    });
  }

  private get webTitle$(): Observable<string | undefined> {
    return this.scrape$.pipe(
      catchError(() => of(<Ref> { url: this.url().value })),
      switchMap(s => this.oembeds.get(s.url).pipe(
        map(oembed => {
          this.oembed.set(oembed!);
          if (oembed) s.title ||= oembed.title || '';
          return s;
        }),
        catchError(() => of(s)),
      )),
      map(s => s.title),
    );
  }

  scrapePublished() {
    this.scrapingPublished.set(true);
    this.scrape$.pipe(
      catchError(err => {
        this.scrapingPublished.set(false);
        // TODO: Write error
        return throwError(() => err);
      })
    ).subscribe(ref => {
      this.scrapingPublished.set(false);
      this.published().setValue(ref.published?.toFormat("YYYY-MM-DD'T'TT"));
    });
  }

  scrapeAll() {
    if (this.oembed()) {
      // TODO: oEmbed
    } else {
      this.scrapingAll.set(true);
      this.scrape$.pipe(
        catchError(err => {
          this.scrapingAll.set(false);
          return throwError(() => err);
        })
      ).subscribe(s => {
        if (!hasMedia(s) || hasMedia(this.group().value)) {
          this.scrapeComment();
        }
        this.scrapePlugins();
        this.scrapingAll.set(false);
      });
    }
  }

  /**
   * Scrapes plugins from the URL. Defaults are merged under the scraped
   * value of each plugin that was found.
   */
  scrapePlugins(url?: string, defaults: Record<string, any> = {}) {
    if (this.oembed()) {
      // TODO: oEmbed
    } else {
      this.scrapeUrl$(url).subscribe(s => {
        for (const t of s.tags || []) {
          if (!hasTag(t, this.tags().value)) this.togglePlugin(t);
        }
        const scraped: Record<string, any> = { ...s.plugins || {} };
        for (const t in scraped) {
          if (defaults[t]) scraped[t] = { ...defaults[t], ...scraped[t] };
        }
        this.ref.update(ref => ({
          ...ref,
          plugins: {
            ...this.group().value.plugins || {},
            ...scraped,
          },
        } as Ref));
      });
    }
  }

  scrapeComment() {
    if (this.oembed()) {
      // TODO: oEmbed
    } else {
      this.scrape$.subscribe(s => this.setComment(s.comment || ''));
    }
  }

  addCompletedUpload(ref: Ref) {
    this.completedUploads.update(uploads => [...uploads, ref]);
  }

  togglePlugin(tag: string) {
    this.toggleTag.emit(tag);
    if (tag) {
      if (hasTag(tag, this.tags().value)) {
        this.tagsFormComponent().removeTagAndChildren(tag);
      } else {
        this.tagsFormComponent().addTag(tag);
      }
    }
  }

  setRef(ref: Partial<Ref>) {
    this.ref.set(ref as Ref);
    setRefForm(this.fb, this.admin, this.group(), ref);
    this.sourcesFormComponent().setLinks(ref.sources || []);
    this.altsFormComponent().setLinks(ref.alternateUrls || []);
  }
}

/**
 * Fills a Ref form from a Ref. Plugin form groups are added for the Ref's tags,
 * and their fields are built from the Ref's plugin data when the form renders.
 */
export function setRefForm(fb: UntypedFormBuilder, admin: AdminService, group: UntypedFormGroup, ref: Partial<Ref>) {
  setArray(fb, group.get('sources') as UntypedFormArray, ref.sources || []);
  setArray(fb, group.get('alternateUrls') as UntypedFormArray, ref.alternateUrls || []);
  setArray(fb, group.get('tags') as UntypedFormArray, ref.tags || []);
  const tags = addAllHierarchicalTags(ref.tags || []);
  const plugins = group.get('plugins') as UntypedFormGroup | null;
  if (plugins) {
    syncPluginsForm(fb, admin, plugins, tags);
    for (const tag in plugins.controls) plugins.controls[tag].reset(ref.plugins?.[tag]);
  } else {
    group.addControl('plugins', pluginsForm(fb, admin, tags));
  }
  group.patchValue({
    ...ref,
    published: ref.published ? ref.published.toFormat("yyyy-MM-dd'T'TT") : undefined,
  });
}

function setArray(fb: UntypedFormBuilder, array: UntypedFormArray | null, values: string[]) {
  if (!array) return;
  while (array.length > values.length) array.removeAt(array.length - 1, { emitEvent: false });
  while (array.length < values.length) array.push(fb.control(''), { emitEvent: false });
  array.setValue(values);
}

export function refForm(fb: UntypedFormBuilder) {
  return fb.group({
    url: { value: '',  disabled: true },
    published: [''],
    modified: [''],
    modifiedString: [''],
    title: [''],
    comment: [''],
    sources: fb.array([]),
    alternateUrls: fb.array([]),
    tags: fb.array([]),
    plugins: fb.group({})
  });
}
